import { arrayOf, number, shape, string } from "prop-types";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import GtmEvent from "web/Layout/Gtm/GtmEvent";

import useGetProductOptions from "web/hooks/useGetProductOptions";
import useGetTourismProductOptions from "web/hooks/useGetTourismProductOptions";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import getPriceFinalPriceDiscount from "web/utils/page/product/universal/getPriceFinalPriceDiscount";
import gtmGetLastPageType from "web/utils/system/GTM/gtmGetLastPageType";

import { pageName } from "web/constants/toolbar";

const checkNewIdsUniq = (
  lastProductImpressionEvent,
  gtmEnhancedEcommOptions,
  lastVirtualPageViewEvent
) => {
  const lastEnventProducts =
    lastProductImpressionEvent?.ecommerce?.impressions?.map(
      (item) => item.id
    ) || [];
  const { date_from, date_to, amount_people } =
    lastProductImpressionEvent?.ecommerce?.impressions?.[0] || {};
  let isAllNewIdsUniq = false;

  if (
    lastVirtualPageViewEvent["gtm.uniqueEventId"] >
    lastProductImpressionEvent["gtm.uniqueEventId"]
  ) {
    isAllNewIdsUniq = true;
  } else if (lastEnventProducts?.length > 0 && gtmEnhancedEcommOptions) {
    const {
      date_from: currDateFrom,
      date_to: currDateTo,
      amount_people: currAmountPeople,
    } = gtmEnhancedEcommOptions?.ecommerce?.impressions?.[0] || {};
    if (
      date_from !== currDateFrom ||
      date_to !== currDateTo ||
      amount_people !== currAmountPeople
    ) {
      return true;
    }
    isAllNewIdsUniq = gtmEnhancedEcommOptions?.ecommerce?.impressions?.every(
      (item) => !lastEnventProducts.includes(item.id)
    );
  } else if (lastEnventProducts?.length === 0) {
    isAllNewIdsUniq = true;
  }

  return isAllNewIdsUniq;
};

const gtmGetProductImpressions = (
  data,
  isAnixe,
  productsOptionsList,
  previousValue,
  lastProductImpressionEvent,
  lastProductPosition,
  currentCgGroup2,
  dateFrom,
  dateTo,
  peopleAmount,
  pageSizeDefault
) => {
  if (!isArrayHasItems(data) || !isArrayHasItems(productsOptionsList))
    return [];

  const { search } = useLocation();
  const paramsSearch = new URLSearchParams(search);
  const pageCurrent = +paramsSearch.get(pageName) || 1;

  const startFromIndex =
    previousValue === data.length
      ? pageSizeDefault * (pageCurrent - 1)
      : previousValue - data.length;
  const currentProducts = lastProductImpressionEvent
    ? data.slice(startFromIndex, data.length)
    : data;

  return currentProducts?.reduce((acc, prod, idx) => {
    let productCategoryPath;
    let productCategoriesPaths;

    productsOptionsList.some((element) => {
      if (element.productId === prod.id) {
        productCategoryPath = element.fullCategoryPath;
        productCategoriesPaths = element.fullCategoriesPaths;
        return true;
      }
      return false;
    });

    const categoriesString = productCategoryPath;

    const myBrand = () => {
      let brandName = "";

      if (isAnixe) {
        brandName = "Anixe";
      } else if (prod?.supplier_name) {
        brandName = prod.supplier_name.replace(/(<([^>]+)>)/gi, "");
      }

      return brandName;
    };

    const { finalPrice } = getPriceFinalPriceDiscount({
      price: prod?.price,
      finalPrice: prod?.final_price,
      options: prod?.product_options,
    });

    const productFinalPrice = () => {
      let productPrice = "";

      if (isAnixe) {
        productPrice = prod?.tourism_anixe_min_offer?.total_price;
      } else if (prod?.final_price) {
        productPrice = finalPrice;
      }

      return `${productPrice}`;
    };

    const productListing = gtmGetLastPageType({ currentCgGroup2 });

    acc.push({
      name: prod?.name || "",
      id: prod?.id || "",
      price: productFinalPrice(),
      brand: myBrand(),
      category: categoriesString,
      variant: "",
      list: productListing?.list,
      position: lastProductPosition + idx + 1,
      ...(isAnixe ? { location_name: prod?.tourism_city || "" } : {}),
      dimension6: productCategoriesPaths,
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
      ...(peopleAmount ? { amount_people: peopleAmount } : {}),
    });
    return acc;
  }, []);
};

const gtmGetOptions = ({
  data,
  currency,
  categories,
  isAnixe = false,
  productsOptionsList,
  previousValue,
  lastProductImpressionEvent,
  isReady,
  lastProductPosition = 0,
  dateFrom,
  dateTo,
  peopleAmount,
  pageSizeDefault,
  cgGroup1Path,
}) => {
  const virtualView = {
    cgroup1: cgGroup1Path || "Kategoria produktowa",
    cgroup2: categories,
  };

  const enhancedEcomm = {
    ecommerce: {
      currencyCode: currency,
      impressions: isReady
        ? gtmGetProductImpressions(
            data,
            isAnixe,
            productsOptionsList,
            previousValue,
            lastProductImpressionEvent,
            lastProductPosition,
            categories,
            dateFrom,
            dateTo,
            peopleAmount,
            pageSizeDefault
          )
        : [],
    },
  };
  return [virtualView, enhancedEcomm];
};

export const ProductsGtms = ({
  data,
  previousValue,
  currency,
  categoriesPath,
  cgGroup1Path,
  pageSizeDefault,
}) => {
  const productsOptionsData = isArrayHasItems(data)
    ? data.map((itemData) => {
        const productObj = {
          productId: itemData.id,
          productCategoryId: itemData?.main_category_id,
          productAttributesId: [{}],
          productCategoriesIds: itemData?.category_ids,
        };
        return productObj;
      })
    : null;

  const [finalProductOptions, isReady] =
    useGetProductOptions(productsOptionsData);

  const dataLayerTemp = window.dataLayer ? [...window.dataLayer].reverse() : [];

  const lastProductImpressionEvent =
    dataLayerTemp.find((element) => element.event === "productImpression") ||
    "";

  const lastVirtualPageViewEvent =
    dataLayerTemp.find((element) => element.event === "virtualPageView") || "";

  const [gtmVirtualPageViewOptions, gtmEnhancedEcommOptions] = useMemo(() => {
    return gtmGetOptions({
      data,
      currency,
      categories: categoriesPath,
      productsOptionsList: finalProductOptions,
      previousValue,
      lastProductImpressionEvent,
      isReady,
      lastProductPosition: previousValue,
      pageSizeDefault,
      cgGroup1Path,
    });
  }, [
    isReady,
    previousValue,
    currency,
    categoriesPath,
    data,
    JSON.stringify(finalProductOptions),
    JSON.stringify(lastProductImpressionEvent),
    pageSizeDefault,
    cgGroup1Path,
  ]);

  const isAllGtmNewIdsUniq = checkNewIdsUniq(
    lastProductImpressionEvent,
    gtmEnhancedEcommOptions,
    lastVirtualPageViewEvent
  );
  const isCorrectCategoryPath =
    gtmVirtualPageViewOptions?.cgroup2 &&
    gtmEnhancedEcommOptions?.ecommerce?.impressions?.[0]?.list?.includes(
      gtmVirtualPageViewOptions?.cgroup2
    );

  return (
    <>
      {gtmVirtualPageViewOptions?.cgroup2 &&
        (lastVirtualPageViewEvent?.cgroup1 !==
          gtmVirtualPageViewOptions?.cgroup1 ||
          lastVirtualPageViewEvent?.cgroup2 !==
            gtmVirtualPageViewOptions?.cgroup2) && (
          <GtmEvent options={gtmVirtualPageViewOptions} withUrl />
        )}
      {isReady &&
        gtmEnhancedEcommOptions?.ecommerce?.impressions?.length > 0 &&
        isCorrectCategoryPath &&
        isAllGtmNewIdsUniq && (
          <GtmEvent
            eventName="productImpression"
            options={gtmEnhancedEcommOptions}
          />
        )}
    </>
  );
};

ProductsGtms.propTypes = {
  data: arrayOf(shape({})),
  previousValue: number.isRequired,
  currency: string,
  categoriesPath: string,
  pageSizeDefault: number.isRequired,
};

ProductsGtms.defaultProps = {
  data: [],
  currency: "",
  categoriesPath: "",
};

export const AnixeTourismProductsGtms = ({
  data,
  ids,
  additionalParameters,
  previousValue,
  currency,
  categoriesPath,
  cgGroup1Path,
  pageSizeDefault,
}) => {
  const productsList =
    data && isArrayHasItems(data) && isArrayHasItems(ids)
      ? ids.reduce((result, id) => {
          const edgeData =
            id && data.find((edge) => edge && edge.id && +edge.id === +id);
          return edgeData ? [...result, edgeData] : result;
        }, [])
      : [];

  const productsOptionsData = isArrayHasItems(productsList)
    ? productsList.map((itemData) => {
        const productObj = {
          productId: itemData.id,
          productCategoryId: itemData?.main_category_id,
          productAttributesId: [{}],
          productCategoriesIds: itemData?.category_ids,
        };
        return productObj;
      })
    : null;

  const [finalProductOptions, isReady] =
    useGetProductOptions(productsOptionsData);

  const dataLayerTemp = window.dataLayer ? [...window.dataLayer].reverse() : [];

  const lastProductImpressionEvent =
    dataLayerTemp.find((element) => element.event === "productImpression") ||
    "";

  const lastVirtualPageViewEvent =
    dataLayerTemp.find((element) => element.event === "virtualPageView") || "";

  const { dateFrom, dateTo, peopleAmount } =
    useGetTourismProductOptions(additionalParameters);

  const [gtmVirtualPageViewOptions, gtmEnhancedEcommOptions] = useMemo(() => {
    return gtmGetOptions({
      data,
      currency,
      categories: categoriesPath,
      isAnixe: true,
      productsOptionsList: finalProductOptions,
      previousValue,
      lastProductImpressionEvent,
      isReady,
      lastProductPosition: previousValue,
      dateFrom,
      dateTo,
      peopleAmount,
      pageSizeDefault,
      cgGroup1Path,
    });
  }, [
    isReady,
    previousValue,
    currency,
    categoriesPath,
    data,
    JSON.stringify(finalProductOptions),
    JSON.stringify(lastProductImpressionEvent),
    dateFrom,
    dateTo,
    peopleAmount,
    pageSizeDefault,
    cgGroup1Path,
  ]);

  const isAllGtmNewIdsUniq = checkNewIdsUniq(
    lastProductImpressionEvent,
    gtmEnhancedEcommOptions,
    lastVirtualPageViewEvent
  );

  return (
    <>
      {gtmVirtualPageViewOptions?.cgroup2 &&
        (lastVirtualPageViewEvent?.cgroup1 !==
          gtmVirtualPageViewOptions?.cgroup1 ||
          lastVirtualPageViewEvent?.cgroup2 !==
            gtmVirtualPageViewOptions?.cgroup2) && (
          <GtmEvent options={gtmVirtualPageViewOptions} withUrl />
        )}
      {isReady &&
        gtmEnhancedEcommOptions?.ecommerce?.impressions?.length > 0 &&
        isAllGtmNewIdsUniq && (
          <GtmEvent
            eventName="productImpression"
            options={gtmEnhancedEcommOptions}
          />
        )}
    </>
  );
};

AnixeTourismProductsGtms.propTypes = {
  data: arrayOf(shape({})),
  ids: arrayOf(number),
  additionalParameters: string,
  previousValue: number.isRequired,
  currency: string,
  categoriesPath: string,
  pageSizeDefault: number.isRequired,
  cgGroup1Path: string,
};

AnixeTourismProductsGtms.defaultProps = {
  data: [],
  ids: [],
  additionalParameters: "",
  currency: "",
  categoriesPath: "",
  cgGroup1Path: "",
};
